import React, { useState, useEffect, useCallback, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import type { ColDef } from "ag-grid-community";
import { toast } from "react-toastify";
import {
  Search,
  Download,
  Eye,
  FileText,
  Plus,
  Filter,
  Calendar,
  DollarSign,
  Clock,
  Wrench,
  ShoppingCart,
  Settings,
  AlertTriangle,
  Trash2,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { usePremisesStore, useRegionStore } from "../../../store/store";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import AddEventModal from "./AddEventModal";
import ViewEventModal from "./ViewEventModal";
import {
  EquipmentHistory,
  getAllEquipmentHistory,
  getEquipmentHistory,
  deleteEquipmentHistory,
} from "../../../service/api";
import images from "../../../assets/images";

interface CostSummary {
  purchaseCost: number;
  maintenanceCost: number;
  totalCost: number;
  eventsCount: number;
}

const getEventTypeIcon = (eventType: string) => {
  switch (eventType) {
    case "Purchase":
      return <ShoppingCart className="w-4 h-4 text-blue-500" />;
    case "Service":
    case "Maintenance":
      return <Settings className="w-4 h-4 text-green-500" />;
    case "Repair":
      return <Wrench className="w-4 h-4 text-orange-500" />;
    case "Calibration":
      return <Settings className="w-4 h-4 text-purple-500" />;
    case "Inspection":
      return <Eye className="w-4 h-4 text-teal-500" />;
    case "Incident":
      return <AlertTriangle className="w-4 h-4 text-red-500" />;
    case "Transfer":
      return <span className="text-gray-500">↔</span>;
    case "Decommission":
      return <Trash2 className="w-4 h-4 text-gray-500" />;
    default:
      return <Clock className="w-4 h-4 text-gray-500" />;
  }
};

const DocumentationAndHistory: React.FC = () => {
  const location = useLocation();
  const [rowData, setRowData] = useState<EquipmentHistory[]>([]);
  const [filteredData, setFilteredData] = useState<EquipmentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEventType, setSelectedEventType] = useState<string>("All");
  const [selectedEquipment, setSelectedEquipment] = useState<string>("All");
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [costSummary, setCostSummary] = useState<CostSummary>({
    purchaseCost: 0,
    maintenanceCost: 0,
    totalCost: 0,
    eventsCount: 0,
  });
  const [isAddEventModalOpen, setIsAddEventModalOpen] = useState(false);
  const [isEditEventModalOpen, setIsEditEventModalOpen] = useState(false);
  const [isViewEventModalOpen, setIsViewEventModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EquipmentHistory | null>(
    null,
  );
  const [editingEvent, setEditingEvent] = useState<EquipmentHistory | null>(
    null,
  );

  const { fetchPremises } = usePremisesStore();
  const { fetchRegions } = useRegionStore();

  // Initialize premises and regions data
  useEffect(() => {
    fetchPremises();
    fetchRegions();
  }, [fetchPremises, fetchRegions]);

  // Get unique equipment list for filter
  const equipmentList = useMemo(() => {
    const equipment = Array.from(
      new Set(rowData.map((item) => item.equipmentName)),
    );
    return ["All", ...equipment];
  }, [rowData]);

  // Get unique event types for filter
  const eventTypes = useMemo(() => {
    const types = Array.from(new Set(rowData.map((item) => item.eventType)));
    return ["All", ...types];
  }, [rowData]);

  const fetchHistoryData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllEquipmentHistory(); // Fetch all data first
      if (response.data && Array.isArray(response.data.message)) {
        setRowData(response.data.message);

        // Apply local filters to the fetched data
        let filtered = response.data.message;

        // Region filter
        if (selectedRegion) {
          filtered = filtered.filter((item) => item.region === selectedRegion);
        }

        // Premises filter
        if (selectedPremises) {
          filtered = filtered.filter(
            (item) => item.premises === selectedPremises,
          );
        }

        // Event Type filter
        if (selectedEventType !== "All") {
          filtered = filtered.filter(
            (item) => item.eventType === selectedEventType,
          );
        }

        // Equipment filter
        if (selectedEquipment !== "All") {
          filtered = filtered.filter(
            (item) => item.equipmentName === selectedEquipment,
          );
        }

        // Search term filter
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          filtered = filtered.filter(
            (item) =>
              item.equipmentName?.toLowerCase().includes(searchLower) ||
              item.eventType?.toLowerCase().includes(searchLower) ||
              item.notes?.toLowerCase().includes(searchLower) ||
              item.eventId?.toLowerCase().includes(searchLower) ||
              item.performedBy?.toLowerCase().includes(searchLower),
          );
        }

        setFilteredData(filtered);

        // Calculate cost summary based on filtered data
        const purchaseCost = filtered
          .filter((event) => event.eventType === "Purchase")
          .reduce((sum, event) => sum + (event.cost || 0), 0);

        const maintenanceCost = filtered
          .filter((event) => event.eventType !== "Purchase")
          .reduce((sum, event) => sum + (event.cost || 0), 0);

        setCostSummary({
          purchaseCost,
          maintenanceCost,
          totalCost: purchaseCost + maintenanceCost,
          eventsCount: filtered.length,
        });
      } else {
        setRowData([]);
        setFilteredData([]);
        console.warn("Unexpected response format:", response);
      }
    } catch (error: any) {
      console.error("Failed to fetch history data:", error);
      toast.error("Failed to load equipment history");
      setRowData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [
    selectedRegion,
    selectedPremises,
    selectedEventType,
    selectedEquipment,
    searchTerm,
  ]);

  useEffect(() => {
    fetchHistoryData();
  }, [fetchHistoryData]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleViewEvent = (event: EquipmentHistory) => {
    setSelectedEvent(event);
    setIsViewEventModalOpen(true);
  };

  const handleEditEvent = (event: EquipmentHistory) => {
    setEditingEvent(event);
    setIsEditEventModalOpen(true);
  };

  const handleDeleteEvent = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      await deleteEquipmentHistory(id);
      toast.success("Event deleted successfully");
      fetchHistoryData(); // Refresh data
    } catch (error: any) {
      console.error("Failed to delete event:", error);
      toast.error("Failed to delete event. Please try again.");
    }
  };

  const handleAddEvent = () => {
    setSelectedEvent(null);
    setIsAddEventModalOpen(true);
  };

  const handleEventSuccess = () => {
    setIsAddEventModalOpen(false);
    setIsEditEventModalOpen(false);
    fetchHistoryData(); // Refresh data
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const columnDefs: ColDef<EquipmentHistory>[] = [
    {
      headerName: "Event ID",
      field: "eventId",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
    },
    {
      headerName: "Equipment",
      field: "equipmentName",
      flex: 1.2,
      headerStyle: { backgroundColor: "#e9f5ff" },
    },
    {
      headerName: "Event Type",
      field: "eventType",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellRenderer: (params: any) => (
        <div className="flex items-center gap-2">
          {getEventTypeIcon(params.value)}
          <span>{params.value}</span>
        </div>
      ),
    },
    {
      headerName: "Event Date",
      field: "eventDate",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Cost",
      field: "cost",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      valueFormatter: (params) => formatCurrency(params.value),
      cellStyle: (params) => ({
        fontWeight: "bold",
        color: params.value ? "#0fa9a6" : "gray",
      }),
    },
    {
      headerName: "Performed By",
      field: "performedBy",
      flex: 1.2,
      headerStyle: { backgroundColor: "#e9f5ff" },
    },
    {
      headerName: "Notes",
      field: "notes",
      flex: 2,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { whiteSpace: "normal" },
      autoHeight: true,
    },
    {
      headerName: "Action",
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellRenderer: (params: any) => {
        const event = params.data;
        return (
          <div className="flex items-center gap-2 justify-center">
            <button
              onClick={() => handleViewEvent(event)}
              title="View Event Details"
              className="p-1 text-blue-600 hover:bg-blue-100 rounded"
            >
              <img src={images.viewIcon} className="w-5" />
            </button>
            <button
              onClick={() => handleEditEvent(event)}
              title="Edit Event"
              className="p-1 text-yellow-600 hover:bg-yellow-100 rounded"
            >
              <img src={images.editIcon} className="w-5" />
            </button>
            <button
              onClick={() => handleDeleteEvent(event.id)}
              title="Delete Event"
              className="p-1 text-red-600 hover:bg-red-100 rounded"
            >
              <Trash2 className="w-5" />
            </button>
            <button
              onClick={() => toast.info("Download functionality coming soon")}
              title="Download Report"
              className="p-1 text-gray-600 hover:bg-gray-100 rounded"
            >
              <Download className="w-5" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      <h1 className="text-2xl font-semibold text-gray-800 mb-6">
        Documentation & History
      </h1>

      {/* Filters and Actions */}
      <div className="flex justify-between gap-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <Dropdown
            selectedValue={selectedRegion}
            onChange={setSelectedRegion}
            type="region"
          />
          <Dropdown
            selectedValue={selectedPremises}
            onChange={setSelectedPremises}
            type="premises"
            selectedRegion={selectedRegion}
          />

          {/* Event Type Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-2.5 text-gray-500 w-4 h-4" />
            <select
              value={selectedEventType}
              onChange={(e) => setSelectedEventType(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
            >
              {eventTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Equipment Filter */}
          <div className="relative">
            <select
              value={selectedEquipment}
              onChange={(e) => setSelectedEquipment(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
            >
              {equipmentList.map((equipment) => (
                <option key={equipment} value={equipment}>
                  {equipment}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
          onClick={handleAddEvent}
        >
          <Plus className="w-5 h-5" />
          ADD EVENT
        </button>
      </div>

      {/* Cost Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 my-8">
        <div className="bg-white rounded-lg shadow p-6 flex justify-between">
          <div>
            <h3 className="text-black text-sm font-semibold mb-1">
              Total Events
            </h3>
            <p className="text-3xl font-bold text-[#3f86c0]">
              {costSummary.eventsCount}
            </p>
          </div>
          {/* <div className="bg-blue-100 p-6 rounded-full">
            <Clock className="w-6 h-6 text-blue-600" />
          </div> */}
        </div>

        {/* <div className="bg-white rounded-lg shadow p-6 flex justify-between">
          <div>
            <h3 className="text-black text-sm font-semibold mb-1">
              Purchase Cost
            </h3>
            <p className="text-3xl font-bold text-[#0fa9a6]">
              {formatCurrency(costSummary.purchaseCost)}
            </p>
          </div>
          <div className="bg-teal-100 p-6 rounded-full">
            <ShoppingCart className="w-6 h-6 text-teal-600" />
          </div>
        </div> */}

        {/* <div className="bg-white rounded-lg shadow p-6 flex justify-between">
          <div>
            <h3 className="text-black text-sm font-semibold mb-1">
              Maintenance Cost
            </h3>
            <p className="text-3xl font-bold text-[#FF9800]">
              {formatCurrency(costSummary.maintenanceCost)}
            </p>
          </div>
          <div className="bg-orange-100 p-6 rounded-full">
            <Settings className="w-6 h-6 text-orange-600" />
          </div>
        </div> */}

        <div className="bg-white rounded-lg shadow p-6 flex justify-between">
          <div>
            <h3 className="text-black text-sm font-semibold mb-1">
              Total Cost of Ownership
            </h3>
            <p className="text-3xl font-bold text-[#F44336]">
              {formatCurrency(costSummary.totalCost)}
            </p>
          </div>
          {/* <div className="bg-red-100 p-3 rounded-full">
            <DollarSign className="w-5 h-5 text-red-600" />
          </div> */}
        </div>
      </div>

      {/* Search and Table */}
      <div className="bg-[#ffffff] w-[100%]">
        <div className="px-3 py-3">
          <div className="relative flex justify-between items-center">
            <div className="flex w-10/12 justify-start items-center">
              <Search className="absolute left-3 text-gray-500" />
              <input
                type="text"
                placeholder="Search by Equipment, Event Type, or Notes"
                className="border-2 border-[#4f5762] mr-5 w-[400px] px-10 py-2 rounded placeholder:font-semibold placeholder:text-[#4e545d]"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>

        <div className="ag-theme-alpine mt-4">
          {loading ? (
            <div className="text-center py-4">Loading history data...</div>
          ) : (
            <AgGridReact
              rowData={filteredData}
              columnDefs={columnDefs}
              rowHeight={60}
              headerHeight={65}
              paginationPageSize={10}
              pagination={true}
              domLayout="autoHeight"
              defaultColDef={{
                sortable: true,
                resizable: true,
              }}
              getRowStyle={(params) => ({
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                marginBottom: "8px",
                borderRadius: "4px",
                backgroundColor: "white",
              })}
            />
          )}
        </div>
      </div>

      {/* View Event Modal */}
      {isViewEventModalOpen && selectedEvent && (
        <ViewEventModal
          isOpen={isViewEventModalOpen}
          onClose={() => setIsViewEventModalOpen(false)}
          event={selectedEvent}
          onEditSuccess={fetchHistoryData}
        />
      )}

      {/* Add Event Modal */}
      {isAddEventModalOpen && (
        <AddEventModal
          isOpen={isAddEventModalOpen}
          onClose={() => setIsAddEventModalOpen(false)}
          onSuccess={handleEventSuccess}
        />
      )}

      {/* Edit Event Modal */}
      {isEditEventModalOpen && editingEvent && (
        <AddEventModal
          isOpen={isEditEventModalOpen}
          onClose={() => setIsEditEventModalOpen(false)}
          onSuccess={handleEventSuccess}
          initialData={editingEvent}
        />
      )}
    </div>
  );
};

export default DocumentationAndHistory;
